import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import SFDateRangeSelector from "../components/SFDateRangeSelector";
import PharmacyHaloButton from "../components/Button";
import { phappsRequest } from "../services/apiService";
import { QUDA_COLORS } from "../config/colors";
import { API_PATHS } from "../config/apiConstants";
import QudaAnalytics from "../components/QudaAnalytics";

const QudaAnalyticsPage = () => {
  const { chemistID } = useParams();
  const navigate = useNavigate();
  const [chemistData, setChemistData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize date range: last 30 days
  const [dateRange, setDateRange] = useState(() => {
    const today = new Date();
    const startDate = new Date();
    startDate.setDate(today.getDate() - 30);
    return [startDate, today];
  });

  useEffect(() => {
    const fetchChemistData = async () => {
      try {
        setLoading(true);

        const [startDate, endDate] = dateRange;

        const params = {
          PHUUID: chemistID,
          DateStart: startDate.toISOString().split("T")[0],
          DateEnd: endDate.toISOString().split("T")[0],
        };

        const response = await phappsRequest(
          API_PATHS.CHEMIST_DATA,
          "GET",
          null,
          params
        );
        if (response && response.ChemistData) {
          setChemistData(response);
        } else {
          setError("Failed to fetch chemist data");
        }
      } catch (err) {
        console.error("Error fetching chemist data:", err);
        setError("Error fetching chemist data");
      } finally {
        setLoading(false);
      }
    };

    if (chemistID) {
      fetchChemistData();
    }
  }, [chemistID, dateRange]);

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
  };

  if (loading) {
    return (
      <Box className="flex items-center justify-center h-64">
        <Typography variant="h6" color="textSecondary">
          Loading Quda analytics...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="flex flex-col items-center justify-center h-64 space-y-4">
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <PharmacyHaloButton onClick={() => navigate("/quda")}>
          <ArrowBack sx={{ mr: 1 }} />
          Back to Quda Dashboard
        </PharmacyHaloButton>
      </Box>
    );
  }

  if (!chemistData || !chemistData.ChemistData) {
    return (
      <Box className="flex flex-col items-center justify-center h-64 space-y-4">
        <Typography variant="h6" color="textSecondary">
          No chemist data available
        </Typography>
        <PharmacyHaloButton onClick={() => navigate("/quda")}>
          <ArrowBack sx={{ mr: 1 }} />
          Back to Quda Dashboard
        </PharmacyHaloButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: `${QUDA_COLORS.TEAL_LIGHT}40`,
        transition: "background-color 0.3s ease",
        p: 3,
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        mx: -2,
        my: -2,
        px: 4,
        py: 4,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", xl: "1fr auto 1fr" },
          gap: 2,
          alignItems: "center",
        }}
      >
        {/* Left: Back Button */}
        <Box sx={{ justifySelf: { xs: "center", xl: "start" } }}>
          <PharmacyHaloButton onClick={() => navigate("/quda")}>
            <ArrowBack sx={{ mr: 1 }} />
            Back to Quda Dashboard
          </PharmacyHaloButton>
        </Box>

        {/* Center: Pharmacy Name */}
        <Box sx={{ justifySelf: "center", textAlign: "center" }}>
          <Typography
            variant="h4"
            className="font-bold"
            style={{ color: QUDA_COLORS.TEAL_DARK }}
          >
            {chemistData.ChemistData.chemistName} - Quda Analytics
          </Typography>
        </Box>

        {/* Right: Date Range Selector */}
        <Box
          sx={{ justifySelf: { xs: "center", xl: "end" }, minWidth: "300px" }}
        >
          <SFDateRangeSelector
            value={dateRange}
            onChange={handleDateRangeChange}
          />
        </Box>
      </Box>

      {/* Quda Analytics Content */}
      <QudaAnalytics
        chemistData={chemistData.ChemistData}
        chemistID={chemistID}
      />
    </Box>
  );
};

export default QudaAnalyticsPage;
